/**
 * commands.js — the terminal command processor.
 *
 * Shared by TerminalSection (inline prompt) and UplinkBar (fixed bottom bar) so
 * both accept exactly the same commands and render the same output. Keeping it
 * here means the components only deal with rendering and focus.
 *
 * An executed command resolves to a list of transcript entries:
 *   { type: 'command' | 'output' | 'help' | 'say' | 'verse' | 'error' | 'reply',
 *     content: string }
 * `type: 'clear'` is a control entry the caller must interpret.
 */

import { askMoony } from './api';
import { VERSES } from './verses';

const pick = (list) => list[Math.floor(Math.random() * list.length)];

/** Snippet length for quoting a visitor's own message back to them. */
const SNIPPET = 60;

export const KNOWN_COMMANDS = new Set([
  'help',
  'sudo',
  'clear',
  'say',
  'moony',
  'now',
  'relay',
  'whoami',
  'time',
  'name',
]);

export const HELP_LINES = [
  'Available commands:',
  '',
  '  help         - A message from the unseen',
  '  clear        - Clear the terminal',
  '  say <msg>    - Transmit a message on the Deadmoon (Telegram)',
  '  moony <msg>  - Talk to the night shift operator',
  '  name <you>   - Change who is transmitting',
  '  now          - What is playing right now',
  '  relay        - Uplink status',
  '  whoami       - ???',
  '  time         - Display current time',
  '',
  'Remember: someone will hear it.',
  'Replies from the group come back here, addressed to you.',
];

function snippet(text) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (!clean) return '';
  return clean.length > SNIPPET ? `${clean.slice(0, SNIPPET - 1)}…` : clean;
}

/**
 * Render an inbound Telegram answer for a human — never a raw message id.
 * Returns a transcript-ready `reply` entry; the `meta` line quotes the
 * visitor's own message instead of the Telegram id it answered.
 */
export function formatIncomingReply(reply) {
  const who = reply.fromBot ? 'the night operator' : reply.from || 'someone in the group';
  const content = `[incoming transmission] ${who} says: "${snippet(reply.text) || '…'}"`;
  const reference = snippet(reply.referenceText);
  const meta = reference ? `↳ answering your "${reference}"` : '↳ answered from the group';
  return { type: 'reply', content, meta };
}

/**
 * The bottom uplink bar is a chat box, not a shell: anything that is not a
 * known command is transmitted as-is. The terminal keeps requiring `say`.
 */
export function normalizeUplinkInput(raw) {
  const line = String(raw || '').trim();
  if (!line) return '';
  const first = line.toLowerCase().split(/\s+/)[0];
  if (KNOWN_COMMANDS.has(first)) return line;
  return `say ${line}`;
}


/** Parse a raw line into `{ name, argument }`. */
export function parseCommand(raw) {
  const line = String(raw || '').trim();
  const lower = line.toLowerCase();
  const space = lower.indexOf(' ');
  if (space === -1) return { name: lower, argument: '', raw: line };
  return {
    name: lower.slice(0, space),
    argument: line.slice(space + 1).trim(),
    raw: line,
  };
}

/**
 * Execute one command.
 *
 * @param {string} raw          the line as typed
 * @param {object} ctx          execution context
 * @param {string} ctx.username       current terminal username
 * @param {string[]} ctx.whoami       whoami answer pool
 * @param {(text:string)=>Promise} ctx.say  relay into Telegram
 * @param {object} ctx.radio          now-playing snapshot from RadioContext
 * @param {object} ctx.relayStatus    Telegram relay capability
 * @param {() => void} ctx.openMoony  open the chatbot modal
 */
export async function executeCommand(raw, ctx) {
  const { name, argument, raw: line } = parseCommand(raw);
  const commandEntry = { type: 'command', content: line };

  if (!line) return [];

  switch (name) {
    case 'sudo':
      // `sudo help` is the v2 entry point; anything else under sudo is help too.
      return [commandEntry, { type: 'help', content: HELP_LINES.join('\n') }];

    case 'help':
    case '--help':
    case '-help':
    case '-h': {
      if (argument === 'commands' || argument === 'sudo') {
        return [commandEntry, { type: 'help', content: HELP_LINES.join('\n') }];
      }
      const verse = pick(VERSES);
      return [commandEntry, { type: 'verse', content: `"${verse.text}"\n\n— ${verse.source}` }];
    }

    case 'clear':
      return [{ type: 'clear' }];

    case 'say': {
      if (!argument) {
        return [commandEntry, { type: 'error', content: 'Usage: say <your message>' }];
      }
      const result = await ctx.say(argument);
      if (!result.ok) {
        const reason = result.relayEnabled ? result.error || 'the uplink refused it' : 'the uplink is not configured';
        return [
          commandEntry,
          { type: 'error', content: `"${argument}" did not leave. ${reason}.` },
        ];
      }
      return [
        commandEntry,
        {
          type: 'say',
          content: `"${argument}" ...transmitted on the Deadmoon.`,
        },
      ];
    }

    case 'name': {
      if (!argument) {
        return [
          commandEntry,
          { type: 'error', content: `Usage: name <you>${ctx.username ? ` (currently ${ctx.username})` : ''}` },
        ];
      }
      await ctx.setName?.(argument);
      return [
        commandEntry,
        { type: 'output', content: `you are transmitting as ${argument}.` },
      ];
    }

    case 'moony': {
      if (!argument) {
        ctx.openMoony();
        return [commandEntry, { type: 'output', content: 'moony is listening in the panel above.' }];
      }
      const { response } = await askMoony({ message: argument, history: [] });
      return [commandEntry, { type: 'reply', content: `moony: ${response}` }];
    }

    case 'now': {
      const { metadata, cleanTitle, isPlaying } = ctx.radio || {};
      const state = isPlaying ? 'playing' : 'paused';
      const listeners = metadata?.listeners ? ` · ${metadata.listeners} listening` : '';
      return [
        commandEntry,
        { type: 'output', content: `${cleanTitle || 'unknown'} (${state})${listeners}` },
      ];
    }

    case 'relay': {
      const status = ctx.relayStatus || {};
      if (!status.checked) {
        return [commandEntry, { type: 'output', content: 'checking the uplink...' }];
      }
      return [
        commandEntry,
        {
          type: 'output',
          content: status.enabled
            ? `telegram uplink: live${status.pollerRunning ? ', listening for replies' : ''}`
            : 'telegram uplink: not configured on this server',
        },
      ];
    }

    case 'whoami': {
      const pool = ctx.whoami?.length ? ctx.whoami : ['you are sleepless'];
      return [commandEntry, { type: 'output', content: pick(pool) }];
    }

    case 'time':
      return [commandEntry, { type: 'output', content: new Date().toLocaleString() }];

    default:
      return [
        commandEntry,
        {
          type: 'error',
          content: `Command not found: ${line}. Type 'sudo help' for available commands.`,
        },
      ];
  }
}
